import React, { useState, useEffect, useRef } from 'react';
import {
  Button,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
  Input,
  Popover,
  PopoverTrigger,
  PopoverContent,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@roger/ui-components';
import {
  FileText,
  Plus,
  ExternalLink,
  Pencil,
  Check,
  X,
  Loader2,
} from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import { useDocumentUrlRefresh } from '../hooks/useDocumentUrlRefresh';

interface Document {
  id: string;
  fileName?: string;
  customName?: string;
  tooltipText?: string; // Optional custom tooltip text
  isPersisted?: boolean; // true = persisted to DB, false = local only (not yet uploaded)
  usedInExtraction?: boolean; // from API: true = used in AI extraction, false = added later
  orderTypeTag?: string | null; // Optional order type tag for intake documents
}

interface PDFViewerNativeProps {
  documents: Document[];
  documentUrls: Record<string, string>;
  activeDocumentId: string;
  onActiveDocumentChange: (id: string) => void;
  onDocumentRename?: (documentId: string, fileName: string) => void;
  onDocumentAdd?: (file: File, displayName: string) => Promise<Document | null>;
  onRefreshUrl?: (documentId: string) => Promise<string | null>;
  onOrderTypeChange?: (documentId: string, orderTypeTag: string) => void;
  orderTypeOptions?: readonly { value: string; label: string }[];
}

const PDFViewerNative: React.FC<PDFViewerNativeProps> = ({
  documents: initialDocuments,
  documentUrls: initialDocumentUrls,
  activeDocumentId,
  onActiveDocumentChange,
  onDocumentRename,
  onDocumentAdd,
  onRefreshUrl,
  onOrderTypeChange,
  orderTypeOptions,
}) => {
  const [localDocuments, setLocalDocuments] = useState<Document[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [editExtension, setEditExtension] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const editInputRef = useRef<HTMLInputElement>(null);
  const blobUrlsRef = useRef<Set<string>>(new Set());
  const localDocsRef = useRef<Document[]>([]);

  // URL refresh hook with debouncing and cancellation
  const {
    documentUrls: localDocumentUrls,
    isRefreshing: isRefreshingUrl,
    handleDebouncedRefresh,
    getUrlForNewTab,
    updateUrls,
    removeUrl,
  } = useDocumentUrlRefresh({
    onRefreshUrl,
    initialUrls: initialDocumentUrls,
  });

  // Keep ref in sync with localDocuments state (avoids stale closure in sync effect)
  useEffect(() => {
    localDocsRef.current = localDocuments;
  }, [localDocuments]);

  // Initialize and sync local documents state with props
  useEffect(() => {
    const currentLocalDocs = localDocsRef.current;

    const propDocs = initialDocuments.map(doc => {
      const existing = currentLocalDocs.find(d => d.id === doc.id);
      return {
        ...doc,
        isPersisted: true, // Documents from props are persisted
        usedInExtraction: doc.usedInExtraction ?? true, // Preserve from API
        // Only preserve existing customName if user has renamed it, don't default to fileName
        customName: existing?.customName,
        // Preserve optimistic orderTypeTag changes so re-fetches don't reset user edits
        orderTypeTag:
          existing !== undefined ? existing.orderTypeTag : doc.orderTypeTag,
      };
    });

    // Create set of prop doc names to detect replaced temp docs
    const propDocNames = new Set(
      propDocs.map(d => d.customName || d.fileName).filter(Boolean)
    );

    // Only keep local-only docs that haven't been replaced by a saved doc
    // This fixes the race condition where temp docs would persist after upload
    const existingLocalOnlyDocs = currentLocalDocs.filter(d => {
      if (d.isPersisted) return false;
      // If there's now a saved doc with the same name, drop this temp doc
      const docName = d.customName || d.fileName;
      return !(docName && propDocNames.has(docName));
    });

    if (currentLocalDocs.length === 0) {
      setLocalDocuments(propDocs);
    } else {
      const mergedDocs = [...propDocs, ...existingLocalOnlyDocs];

      const currentKey = currentLocalDocs
        .map(d => `${d.id}:${d.orderTypeTag ?? ''}`)
        .join(',');
      const newKey = mergedDocs
        .map(d => `${d.id}:${d.orderTypeTag ?? ''}`)
        .join(',');
      if (currentKey !== newKey) {
        setLocalDocuments(mergedDocs);
      }
    }
  }, [initialDocuments]);

  // Cleanup blob URLs on unmount
  useEffect(() => {
    const blobUrls = blobUrlsRef.current;
    return () => {
      blobUrls.forEach(url => URL.revokeObjectURL(url));
    };
  }, []);

  // Focus input when editing starts
  useEffect(() => {
    if (editingId && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingId]);

  // Helper function to extract extension from filename
  const getExtension = (filename: string): string => {
    const lastDotIndex = filename.lastIndexOf('.');
    if (lastDotIndex === -1 || lastDotIndex === 0) {
      return ''; // No extension or filename starts with dot
    }
    return filename.substring(lastDotIndex);
  };

  // Helper function to get base name without extension
  const getBaseName = (filename: string): string => {
    const lastDotIndex = filename.lastIndexOf('.');
    if (lastDotIndex === -1 || lastDotIndex === 0) {
      return filename; // No extension or filename starts with dot
    }
    return filename.substring(0, lastDotIndex);
  };

  const startEditing = (doc: Document, e: React.MouseEvent) => {
    e.stopPropagation();
    const fullName = doc.customName || doc.fileName || '';
    const extension = getExtension(fullName);
    const baseName = getBaseName(fullName);

    setEditingId(doc.id);
    setEditValue(baseName);
    setEditExtension(extension);
  };

  const saveEdit = () => {
    if (editingId && editValue.trim()) {
      const doc = localDocuments.find(d => d.id === editingId);
      // Append extension back to the edited name
      const newName = editValue.trim() + editExtension;

      setLocalDocuments(prev =>
        prev.map(d => (d.id === editingId ? { ...d, customName: newName } : d))
      );

      // Persist to backend if document is from the database
      if (doc?.isPersisted && onDocumentRename) {
        onDocumentRename(editingId, newName);
      }
    }
    setEditingId(null);
    setEditValue('');
    setEditExtension('');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditValue('');
    setEditExtension('');
  };

  // Handle document change - clear stale URL and fetch fresh one from server
  const handleDocumentChange = (documentId: string) => {
    // Immediately update the active document for responsive UI
    onActiveDocumentChange(documentId);

    // Clear the existing (potentially stale) URL so we don't show it
    // This will show a loading state until fresh URL arrives from server
    const doc = localDocuments.find(d => d.id === documentId);
    if (doc?.isPersisted) {
      removeUrl(documentId);
    }

    // Fetch fresh URL from server (debounced to handle rapid switching)
    handleDebouncedRefresh(documentId, { isPersisted: doc?.isPersisted });
  };

  // Handle opening document in new tab with fresh URL
  const handleOpenInNewTab = async () => {
    if (!activeDocumentId) return;
    const doc = localDocuments.find(d => d.id === activeDocumentId);
    const freshUrl = await getUrlForNewTab(activeDocumentId, {
      isPersisted: doc?.isPersisted,
    });
    if (freshUrl) {
      window.open(freshUrl, '_blank');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      saveEdit();
    } else if (e.key === 'Escape') {
      cancelEdit();
    }
  };

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      alert('Please upload a PDF file');
      return;
    }

    // Create a temporary ID for immediate UI feedback
    const tempId = uuidv4();
    const newUrl = URL.createObjectURL(file);
    blobUrlsRef.current.add(newUrl);

    // Generate display name like "PDF 1.pdf", "PDF 2.pdf", etc.
    const pdfNumber = localDocuments.length + 1;
    const displayName = `PDF ${pdfNumber}.pdf`;

    // Add document immediately with isPersisted: false (shows amber dot)
    const tempDoc: Document = {
      id: tempId,
      fileName: displayName,
      customName: displayName,
      isPersisted: false,
    };

    setLocalDocuments(prev => [...prev, tempDoc]);
    updateUrls({ [tempId]: newUrl });
    onActiveDocumentChange(tempId);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    // Upload to backend if callback provided
    if (onDocumentAdd) {
      try {
        const savedDoc = await onDocumentAdd(file, displayName);
        if (savedDoc) {
          // Replace temp document with saved document from server
          setLocalDocuments(prev =>
            prev.map(d =>
              d.id === tempId
                ? { ...savedDoc, customName: displayName, isPersisted: true }
                : d
            )
          );
          // Update URL mapping with new ID (replace temp ID with server ID)
          updateUrls({ [savedDoc.id]: newUrl });
          removeUrl(tempId);
          onActiveDocumentChange(savedDoc.id);
        }
      } catch (err) {
        console.error('Error uploading document:', err);
        // Keep the temp document but it will remain amber (not persisted)
      }
    }
  };

  const activeDoc =
    localDocuments.find(d => d.id === activeDocumentId) || localDocuments[0];
  const activeUrl = activeDoc ? localDocumentUrls[activeDoc.id] : null;

  return (
    <TooltipProvider delayDuration={300}>
      <div className='h-full flex-1 overflow-hidden flex flex-col bg-background'>
        {/* Single Row: Tabs + Actions */}
        <div className='border-b bg-muted/20 flex items-center h-10'>
          {/* Scrollable Tab Container */}
          <div
            className='flex-1 overflow-x-auto min-w-0'
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            <style>{`.hide-scrollbar::-webkit-scrollbar { display: none; }`}</style>
            <div className='flex items-center gap-1 flex-nowrap hide-scrollbar'>
              {localDocuments.map(doc => {
                const displayName =
                  doc.customName || doc.fileName || 'Untitled';
                const isActive = doc.id === activeDocumentId;
                const isEditing = editingId === doc.id;

                if (isEditing) {
                  return (
                    <div
                      key={doc.id}
                      className='flex items-center gap-1 h-8 px-2 bg-background border border-border rounded flex-shrink-0'
                      onClick={e => e.stopPropagation()}
                    >
                      <div className='flex items-center gap-0.5'>
                        <Input
                          ref={editInputRef}
                          value={editValue}
                          onChange={e => setEditValue(e.target.value)}
                          onKeyDown={handleKeyDown}
                          onBlur={saveEdit}
                          className='h-6 w-28 text-xs px-1.5'
                        />
                        {editExtension && (
                          <span className='text-xs text-muted-foreground'>
                            {editExtension}
                          </span>
                        )}
                      </div>
                      <button
                        onClick={saveEdit}
                        className='p-0.5 hover:bg-muted rounded'
                      >
                        <Check className='h-3 w-3 text-emerald-600' />
                      </button>
                      <button
                        onClick={cancelEdit}
                        className='p-0.5 hover:bg-muted rounded'
                      >
                        <X className='h-3 w-3 text-muted-foreground' />
                      </button>
                    </div>
                  );
                }

                const tabButton = (
                  <button
                    onClick={() => handleDocumentChange(doc.id)}
                    className={`
                      group flex items-center gap-1.5 h-8 px-2.5 rounded text-xs font-medium transition-all flex-shrink-0
                      ${
                        isActive
                          ? 'bg-background shadow-sm border border-border text-foreground'
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                      }
                    `}
                  >
                    <div
                      className={`
                        h-1.5 w-1.5 rounded-full flex-shrink-0
                        ${doc.isPersisted && doc.usedInExtraction ? 'bg-emerald-500' : 'bg-amber-500'}
                      `}
                    />
                    <span className='truncate max-w-[80px]'>{displayName}</span>
                    <span
                      role='button'
                      tabIndex={0}
                      onClick={e => startEditing(doc, e)}
                      onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          startEditing(doc, e as unknown as React.MouseEvent);
                        }
                      }}
                      className='p-0.5 rounded opacity-0 group-hover:opacity-100 hover:bg-muted transition-opacity cursor-pointer'
                      title='Rename'
                    >
                      <Pencil className='h-3 w-3 text-muted-foreground' />
                    </span>
                  </button>
                );

                if (onOrderTypeChange && orderTypeOptions) {
                  return (
                    <Popover key={doc.id}>
                      <PopoverTrigger asChild>{tabButton}</PopoverTrigger>
                      <PopoverContent
                        side='bottom'
                        className='w-56 p-3'
                        onClick={e => e.stopPropagation()}
                        onInteractOutside={e => {
                          // Prevent popover from closing when interacting with Select portal
                          const target = e.target as HTMLElement;
                          if (
                            target.closest(
                              '[role="listbox"], [role="option"], [data-radix-select-viewport]'
                            )
                          ) {
                            e.preventDefault();
                          }
                        }}
                      >
                        <p className='text-xs font-medium mb-0.5 truncate'>
                          {doc.tooltipText || displayName}
                        </p>
                        {!doc.isPersisted && (
                          <p className='text-[10px] text-amber-500 mb-2'>
                            Uploading...
                          </p>
                        )}
                        {doc.isPersisted && !doc.usedInExtraction && (
                          <p className='text-[10px] text-amber-500 mb-2'>
                            Added after extraction
                          </p>
                        )}
                        <label className='text-[10px] text-muted-foreground'>
                          Order type
                        </label>
                        <Select
                          value={doc.orderTypeTag || 'none'}
                          onValueChange={val => {
                            const newTag = val === 'none' ? '' : val;
                            // Optimistic local update
                            setLocalDocuments(prev =>
                              prev.map(d =>
                                d.id === doc.id
                                  ? { ...d, orderTypeTag: newTag || null }
                                  : d
                              )
                            );
                            onOrderTypeChange(doc.id, newTag);
                          }}
                        >
                          <SelectTrigger selectSize='sm' className='mt-1'>
                            <SelectValue placeholder='Select order type' />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value='none'>No order type</SelectItem>
                            {orderTypeOptions.map(opt => (
                              <SelectItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </PopoverContent>
                    </Popover>
                  );
                }

                return (
                  <Tooltip key={doc.id}>
                    <TooltipTrigger asChild>{tabButton}</TooltipTrigger>
                    <TooltipContent side='bottom' className='text-xs'>
                      {doc.tooltipText || displayName}
                      {!doc.isPersisted && (
                        <span className='text-amber-500 ml-1'>
                          (Uploading...)
                        </span>
                      )}
                      {doc.isPersisted && !doc.usedInExtraction && (
                        <span className='text-amber-500 ml-1'>
                          (Added after extraction)
                        </span>
                      )}
                    </TooltipContent>
                  </Tooltip>
                );
              })}
            </div>
          </div>

          {/* Action Buttons - Always visible */}
          <div className='flex items-center gap-1 flex-shrink-0 pl-2 border-l border-border/50 ml-2'>
            <input
              type='file'
              ref={fileInputRef}
              className='hidden'
              accept='application/pdf'
              onChange={handleFileUpload}
            />
            <Button
              variant='ghost'
              size='sm'
              className='h-7 text-xs gap-1 px-2'
              onClick={() => fileInputRef.current?.click()}
            >
              <Plus className='h-3.5 w-3.5' />
              Add
            </Button>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant='ghost'
                  size='sm'
                  onClick={handleOpenInNewTab}
                  disabled={!activeUrl || isRefreshingUrl}
                  className='h-7 w-7 p-0'
                >
                  <ExternalLink className='h-3.5 w-3.5 text-muted-foreground' />
                </Button>
              </TooltipTrigger>
              <TooltipContent side='bottom' className='text-xs'>
                Open in new tab
              </TooltipContent>
            </Tooltip>
          </div>
        </div>

        {/* Content Area */}
        <div className='flex-1 overflow-hidden relative bg-muted/10'>
          {activeUrl ? (
            <iframe
              key={activeDocumentId}
              src={`${activeUrl}#navpanes=0`}
              className='w-full h-full border-0'
              title={
                activeDoc?.customName || activeDoc?.fileName || 'PDF Document'
              }
            />
          ) : activeDocumentId ? (
            // Document selected but URL is loading
            <div className='flex-1 h-full flex flex-col items-center justify-center text-muted-foreground gap-3'>
              <Loader2 className='h-8 w-8 animate-spin opacity-50' />
              <p className='text-sm'>Loading document...</p>
            </div>
          ) : (
            // No document selected
            <div className='flex-1 h-full flex flex-col items-center justify-center text-muted-foreground gap-2'>
              <FileText className='h-8 w-8 opacity-20' />
              <p className='text-sm'>Select a document to view</p>
            </div>
          )}
        </div>
      </div>
    </TooltipProvider>
  );
};

export default PDFViewerNative;
